/* eslint-disable camelcase */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'

import { approveJoinRequest, deleteJoinRequest, listJoinRequests, listMyJoinRequests } from '@/api/enterprise'
import { fetchAllUsersThunk } from '@/store/features/member/memberSlice'
import { captureEvent } from '@/utils/captureEvent'
import { EventName, PLATFORM_ADMIN_PORTAL } from '@/utils/eventDefinitions'

interface JoinRequestState {
  joinRequests: JoinRequest[]
  myJoinRequests: JoinRequest[]
  isLoading: boolean
  hasLoaded: boolean
  isMyJoinRequestsLoading: boolean
  hasMyJoinRequestsLoaded: boolean
  error: string | null
}

export interface JoinRequest {
  id: string
  userId: string
  userEmail: string
  userFirstName: string
  userLastName: string
  userAvatarUrl: string
  enterpriseId: string
  createdAt: string
  modifiedAt: string
}

const initialState: JoinRequestState = {
  joinRequests: [],
  myJoinRequests: [],
  isLoading: false,
  hasLoaded: false,
  isMyJoinRequestsLoading: false,
  hasMyJoinRequestsLoaded: false,
  error: null,
}

// TODO: Only try to fetch join requests if you are an admin
export const fetchJoinRequestsThunk = createAsyncThunk(
  'joinRequest/fetchJoinRequests',
  async (_, { rejectWithValue }) => {
    try {
      const response = await listJoinRequests()
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const fetchMyJoinRequestsThunk = createAsyncThunk(
  'joinRequest/fetchMyJoinRequests',
  async (_, { rejectWithValue }) => {
    try {
      const response = await listMyJoinRequests()
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const joinRequestSlice = createSlice({
  name: 'joinRequest',
  initialState,
  reducers: {
    clearJoinRequests: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch join requests cases
      .addCase(fetchJoinRequestsThunk.fulfilled, (state, action) => {
        state.joinRequests = action.payload.map((request) => ({
          id: request.id,
          userId: request.user_id,
          userEmail: request.user_email,
          userFirstName: request.user_first_name,
          userLastName: request.user_last_name,
          userAvatarUrl: request.user_avatar_url,
          enterpriseId: request.enterprise_id,
          createdAt: request.created_at,
          modifiedAt: request.modified_at,
        }))
        state.isLoading = false
        state.hasLoaded = true
        state.error = null
      })
      .addCase(fetchJoinRequestsThunk.pending, (state) => {
        state.isLoading = true
        state.hasLoaded = false
        state.error = null
      })
      .addCase(fetchJoinRequestsThunk.rejected, (state, action) => {
        state.isLoading = false
        state.hasLoaded = false
        state.error = action.payload as string
      })
      // Approve join request cases
      .addCase(approveJoinRequestThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(approveJoinRequestThunk.fulfilled, (state, action) => {
        state.isLoading = false
        // Remove the approved request from the list
        state.joinRequests = state.joinRequests.filter((request) => request.id !== action.payload)
      })
      .addCase(approveJoinRequestThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Delete join request cases
      .addCase(deleteJoinRequestThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteJoinRequestThunk.fulfilled, (state, action) => {
        state.isLoading = false
        // Remove the deleted request from the list
        state.joinRequests = state.joinRequests.filter((request) => request.id !== action.payload)
      })
      .addCase(deleteJoinRequestThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Fetch my join requests cases
      .addCase(fetchMyJoinRequestsThunk.fulfilled, (state, action) => {
        state.myJoinRequests = action.payload.map((request) => ({
          id: request.id,
          userId: request.user_id,
          userEmail: request.user_email,
          userFirstName: request.user_first_name,
          userLastName: request.user_last_name,
          userAvatarUrl: request.user_avatar_url,
          enterpriseId: request.enterprise_id,
          createdAt: request.created_at,
          modifiedAt: request.modified_at,
        }))
        state.isMyJoinRequestsLoading = false
        state.hasMyJoinRequestsLoaded = true
        state.error = null
      })
      .addCase(fetchMyJoinRequestsThunk.pending, (state) => {
        state.isMyJoinRequestsLoading = true
        state.hasMyJoinRequestsLoaded = false
        state.error = null
      })
      .addCase(fetchMyJoinRequestsThunk.rejected, (state, action) => {
        state.isMyJoinRequestsLoading = false
        state.hasMyJoinRequestsLoaded = false
        state.error = action.payload as string
      })
  },
})

export const approveJoinRequestThunk = createAsyncThunk(
  'joinRequest/approveJoinRequest',
  async (requestId: string, { dispatch, rejectWithValue }) => {
    try {
      await approveJoinRequest({ join_request_id: requestId })
      captureEvent(EventName.JoinRequestApproved, {
        request_id: requestId,
        success: true,
        platform: PLATFORM_ADMIN_PORTAL,
      })
      // Refresh members list after approval to show new member
      dispatch(fetchAllUsersThunk())
      return requestId
    } catch (error) {
      captureEvent(EventName.JoinRequestApproved, {
        request_id: requestId,
        success: false,
        platform: PLATFORM_ADMIN_PORTAL,
      })
      return rejectWithValue(error)
    }
  }
)

export const deleteJoinRequestThunk = createAsyncThunk(
  'joinRequest/deleteJoinRequest',
  async (requestId: string, { rejectWithValue }) => {
    try {
      await deleteJoinRequest({ join_request_id: requestId })
      captureEvent(EventName.JoinRequestDismissed, {
        request_id: requestId,
        success: true,
        platform: PLATFORM_ADMIN_PORTAL,
      })
      return requestId
    } catch (error) {
      captureEvent(EventName.JoinRequestDismissed, {
        request_id: requestId,
        success: false,
        platform: PLATFORM_ADMIN_PORTAL,
      })
      return rejectWithValue(error)
    }
  }
)

// Selectors
export const selectJoinRequests = (state: RootState) => state.joinRequest.joinRequests
export const selectIsJoinRequestsLoading = (state: RootState) => state.joinRequest.isLoading
export const selectHasJoinRequestsLoaded = (state: RootState) => state.joinRequest.hasLoaded
export const selectJoinRequestsError = (state: RootState) => state.joinRequest.error
export const selectMyJoinRequests = (state: RootState) => state.joinRequest.myJoinRequests
export const selectIsMyJoinRequestsLoading = (state: RootState) => state.joinRequest.isMyJoinRequestsLoading
export const selectHasMyJoinRequestsLoaded = (state: RootState) => state.joinRequest.hasMyJoinRequestsLoaded

export const { clearJoinRequests } = joinRequestSlice.actions

export default joinRequestSlice.reducer
